import { canQueryDashboardOrderSlice, hasPermission, PERM } from '../accessPermissions';
import { shouldShowContractPendingTab, shouldShowDeliveringPendingTab } from '../dashboard/dashboardOrderPendingTabs';

/** 订单列表入口。普通用户走订单跟进，其余角色走工作台列表。 */
export const getOrderListPath = (userInfo) => {
	if (userInfo?.role === 'user') return '/user/orderlist';
	return '/orderlist';
};

/** 列表标题与订单归属一致：全部订单、主管链、助理、已审批。 */
export function getOrderListHeading(userInfo) {
	if (hasPermission(userInfo, PERM.ORDER_QUERY_TENANT_ALL)) {
		return { pageTitle: '全部订单', subtitle: '系统所有订单' };
	}
	const role = userInfo?.role;
	if (role === 'sale' || role === 'manager') {
		return { pageTitle: '我的销售订单', subtitle: '本人及下属的已审批订单' };
	}
	if (role === 'assistant') {
		return { pageTitle: '助理订单', subtitle: '我创建与指派的订单' };
	}
	if (role === 'finance' || role === 'buyer') {
		return { pageTitle: '已审批订单', subtitle: '已审批的订单' };
	}
	if (role === 'user') {
		return { pageTitle: '我的订单', subtitle: '本人相关订单' };
	}
	return { pageTitle: '全部订单', subtitle: '系统所有订单' };
}

export const getRoleOrderListPath = getOrderListPath;

/** 订单编辑（草稿）列表：全角色统一 /editlist */
export const ORDER_EDIT_LIST_PATH = '/editlist';

/** @param {'drafts'} [tab] 打开指定 tab；省略则默认草稿 */
export const getRoleOrderEditListPath = (tab) => {
	if (tab === 'drafts') {
		return `${ORDER_EDIT_LIST_PATH}?tab=${tab}`;
	}
	// 兼容旧链接：驳回已迁至首页「被驳回」待办
	if (tab === 'rejected') {
		return '/dashboard?pendingTab=Rejected';
	}
	return ORDER_EDIT_LIST_PATH;
};

/** 订单详情页（与列表 Modal 内容同源的页面版） */
export const getOrderDetailPath = (orderOid) => `/order/detail/${orderOid}`;

/** 从「审批驳回」列表进详情；详情页须强制刷新，避免沿用申请中缓存 */
export const REJECTED_DETAIL_FROM_LIST = 'rejectedList';

/** 审批驳回后回到建单页再改再提交 */
export const getRejectedOrderEditPath = (orderOid) => `/order/create/${orderOid}/rejected`;

/** 从待办「申请中」进入驳回编辑；退出时回仪表盘而非编辑列表 */
export const REJECTED_EDIT_FROM_APPLYING_PENDING = 'applyingPending';

/** 报价查询：与「新建订单」成对开放；全角色统一 /quotationlist */
export const QUOTATION_LIST_PATH = '/quotationlist';

/** 新建报价单（报价模式建单页，不含下单） */
export const QUOTATION_CREATE_PATH = '/order/create/new/quotation';

export const getRoleQuotationListPath = () => QUOTATION_LIST_PATH;

const hasUnpaidPrepay = (order) => {
	if (order?.paymentStatus !== 'waitingPayPlaceOrder' || order?.isPlaceOrderPaid) return false;
	const rows = Array.isArray(order?.accountsReceivables) ? order.accountsReceivables : [];
	if (rows.length === 0) return true;
	return rows.some((row) => row?.title === '预付' && !row.isPaid && Number(row.accountsReceivable) > 0);
};

const isWaitingDeliveryAfterPrepay = (order) => {
	if (!['approved', 'systemApproved'].includes(order?.approvalStatus)) return false;
	if (order?.deliveryStatus !== 'waitingDelivery') return false;
	if (order?.isPlaceOrderPaid) return true;
	const rows = Array.isArray(order?.accountsReceivables) ? order.accountsReceivables : [];
	return rows.some((row) => row?.title === '预付' && row.isPaid);
};

/**
 * 只跳到这个人首页看得到的栏。
 * 审批中：有「申请中」就进申请中，否则进「待审批」。
 * 待发货阶段：只有待签约进待签约；有待发货时进待发货（两边都有时也进待发货）。
 */
const resolveFollowPendingTabByAccess = (order, userInfo) => {
	const status = order?.approvalStatus;
	if (status === 'inProcessOfApproval') {
		if (canQueryDashboardOrderSlice(userInfo, PERM.DASHBOARD_ORDER_APPLING)) return 'Applying';
		if (canQueryDashboardOrderSlice(userInfo, PERM.DASHBOARD_ORDER_APPROVING)) return 'approvalPending';
		return null;
	}
	if (status !== 'approved' && status !== 'systemApproved') return null;
	if (hasUnpaidPrepay(order)) {
		if (canQueryDashboardOrderSlice(userInfo, PERM.DASHBOARD_ORDER_PREPAYMENT)) return 'prePaymentOrder';
		return null;
	}
	if (!isWaitingDeliveryAfterPrepay(order)) return null;
	const canContract = shouldShowContractPendingTab(userInfo);
	const canDeliver = shouldShowDeliveringPendingTab(userInfo);
	if (canContract && !canDeliver) return 'ContractPending';
	if (canDeliver) return 'deliveryPending';
	return null;
};

/** 新建成功后「跟进」应打开的首页待办 Tab。对不上栏位时由调用方回订单列表。 */
export const resolveFollowPendingTab = (order, userInfo) => resolveFollowPendingTabByAccess(order, userInfo);

/** 跟进到首页待办；对不上栏位时回订单列表 */
export const getOrderFollowPendingPath = (order, userInfo) => {
	const tab = resolveFollowPendingTab(order, userInfo);
	if (!tab) return getOrderListPath(userInfo);
	const params = new URLSearchParams({ pendingTab: tab });
	if (order?.orderId) params.set('pendingOrder', String(order.orderId));
	return `/dashboard?${params.toString()}`;
};
